const MIN_IDEMPOTENCY_KEY_LENGTH = 6;
const MAX_IDEMPOTENCY_KEY_LENGTH = 200;

export function normalizeOrderIdempotencyKey(value) {
  const normalized = String(value || '').trim();
  if (
    normalized.length < MIN_IDEMPOTENCY_KEY_LENGTH ||
    normalized.length > MAX_IDEMPOTENCY_KEY_LENGTH
  ) {
    return '';
  }
  return normalized;
}

export function createOrderIdempotencyKey({
  nowMs = Date.now(),
  randomId,
} = {}) {
  const numericNow = Number(nowMs);
  const timestamp = Number.isFinite(numericNow) && numericNow >= 0
    ? Math.trunc(numericNow).toString(36)
    : Date.now().toString(36);
  let entropy = String(randomId || '').trim();

  if (!entropy) {
    try {
      entropy = globalThis.crypto?.randomUUID?.() || '';
    } catch (_) {
      entropy = '';
    }
  }
  if (!entropy) entropy = Math.random().toString(36).slice(2, 18);

  const safeEntropy = entropy.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64);
  const candidate = normalizeOrderIdempotencyKey(`ord-${timestamp}-${safeEntropy}`);
  if (candidate) return candidate;

  return `ord-${timestamp}-fallback`;
}

export function resolveOrderIdempotencyKey(existingValue, options = {}) {
  const existing = normalizeOrderIdempotencyKey(existingValue);
  if (existing) {
    return { idempotencyKey: existing, created: false };
  }
  return {
    idempotencyKey: createOrderIdempotencyKey(options),
    created: true,
  };
}

// Owner-approved recovery starts a separate order. Never reset or relabel the
// earlier order/attempt: its callback may still arrive and both may be paid.
export function persistFreshCheckoutSession(payload, {
  previousOrderKey, reason, storage = globalThis.localStorage, nowMs = Date.now(), randomId,
} = {}) {
  const previousKey = normalizeOrderIdempotencyKey(previousOrderKey);
  if (!previousKey) throw new Error('previous_checkout_identity_missing');
  const key = createOrderIdempotencyKey({ nowMs, randomId });
  if (key === previousKey) throw new Error('fresh_checkout_identity_collision');
  const next = { ...payload };
  const previousRecordId = next.airtable_record_id;
  const previousOrderId = next.airtable_order_id;
  for (const field of ['airtable_record_id', 'airtable_order_id', 'order', 'orderId', 'order_id',
    'orderNumber', 'order_number', 'checkout_episode_id', 'paid', 'invrec', 'invrec_num', 'invrec_link',
    'cloudinary', 'dropbox', 'uploads', 'cartUploads', 'worksheet', 'dropbox_folder',
    'payment_attempt_key', 'paymentAttemptKey', 'checkout_id', 'payment_url', 'payment_status_url']) delete next[field];
  const financial = { ...(next.financial || {}) };
  for (const field of ['paid', 'invrec', 'invrec_num', 'invrec_link', 'invoice_number', 'invoice_url',
    'dropbox_shared_link', 'dropbox_worksheet_link', 'dropbox_worksheet_links', 'checkout_confirmed_at',
    'payment_receipt_id', 'provider_document_number', 'job_id_in_main']) delete financial[field];
  const startedAt = new Date(nowMs).toISOString();
  next.idempotency_key = key;
  next.financial = financial;
  next.tracking = { ...(next.tracking || {}), metadata: { ...(next.tracking?.metadata || {}), conversionTime: startedAt } };
  next.payment_recovery = { previous_order_idempotency_key: previousKey,
    ...(previousRecordId ? { previous_airtable_record_id: previousRecordId } : {}),
    ...(previousOrderId ? { previous_order_id: previousOrderId } : {}),
    reason: ['prior_attempt_issued', 'prior_attempt_unverified', 'checkout_details_changed', 'order_terms_changed'].includes(reason)
      ? reason : 'continuation_unavailable',
    risk_acceptance: 'owner-approved-2026-11-03', started_at: startedAt };
  // This must succeed synchronously before refs/state change or a new order is
  // saved. React state updaters alone do not establish this ordering.
  storage.setItem('order_payload', JSON.stringify(next));
  return next;
}
